import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { loginUser } from './api';
import { FiUser, FiLock } from 'react-icons/fi'; // Import icons

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: linear-gradient(135deg, #1c4e8c, #4a90e2);
  padding: 20px;
  direction: rtl;


  @media (max-width: 768px){
    padding: 0px;
  }
`;

const LoginCard = styled.div`
  background-color: #ffffff;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.3s ease-in-out;

  &:hover {
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    width: 90%; /* Make the card take more space on smaller screens */
    padding: 20px;
  }  
`;

const Title = styled.h1`
  color: #1c4e8c;
  font-size: 26px;
  margin-bottom: 5px;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 22px;
  }  
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 13px;
  }  
`;

const InputGroup = styled.div`
  width: 100%;
  margin-bottom: 15px;
  position: relative;
  
`;

const Input = styled.input`
  width: 85%;
  padding: 12px 12px 12px 40px; 
  border: 1px solid ${({ hasError }) => (hasError ? 'red' : '#ccc')};
  border-radius: 6px;
  font-size: 16px;
  outline: none;
  transition: border 0.3s, box-shadow 0.3s;

  &:focus {
    border-color: #1c4e8c;
    box-shadow: 0 0 5px rgba(28, 78, 140, 0.5);
  }

  @media (max-width: 768px) {
    width: 85%;
    padding: 10px 10px 10px 30px;
    font-size: 14px;
  }  
`;

const Icon = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ hasError }) => (hasError ? 'red' : '#666')};

  @media (max-width: 768px) {
    left: 8px;
    font-size: 16px;
  }  
`;

const RememberMeContainer = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  margin-bottom: 15px;
  color: #1c4e8c;
  user-select: none;

  @media (max-width: 768px) {
    font-size: 13px;
  }  
`;

const Checkbox = styled.input`
  margin-left: 10px;
  width: 16px;
  height: 16px;
  cursor: pointer;

  @media (max-width: 768px) {
    width: 14px;
    height: 14px;
  }  
`;

const Button = styled.button`
  background: linear-gradient(135deg, #1c4e8c, #4a90e2);
  color: white;
  border: none;
  padding: 12px 20px;
  font-size: 18px;
  cursor: pointer;
  width: 100%;
  border-radius: 6px;
  transition: 0.3s;

  &:hover {
    background: linear-gradient(135deg, #164b7a, #367bc3);
    transform: scale(1.03);
  }

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 10px;
  }
`;

const ErrorMessage = styled.p`
  color: red;
  margin-top: 10px;
  font-size: 14px;

    @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const Login = ({ setUserData }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // **On Load: Check if user had "Remember Me" checked**
  useEffect(() => {
    const savedUsername = localStorage.getItem('rememberedUsername');
    const savedPassword = localStorage.getItem('rememberedPassword');
    const rememberMeStatus = localStorage.getItem('rememberMe') === 'true';

    if (savedUsername && savedPassword && rememberMeStatus) {
      setUsername(savedUsername);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim()) {
      setError('נא להזין שם משתמש');
      return;
    }

    if (!password.trim()) {
      setError('נא להזין סיסמה');
      return;
    }

    const data = await loginUser(username, password);

    if (data && data.Status === 1) {
      localStorage.setItem('userData', JSON.stringify(data));
      setUserData(data);
      navigate('/warehouses');

      // **If "Remember Me" is checked, store credentials**
      if (rememberMe) {
        localStorage.setItem('rememberedUsername', username);
        localStorage.setItem('rememberedPassword', password);
        localStorage.setItem('rememberMe', 'true');
      } else {
        // **Clear saved credentials if "Remember Me" is unchecked**
        localStorage.removeItem('rememberedUsername');
        localStorage.removeItem('rememberedPassword');
        localStorage.removeItem('rememberMe');
      }
    } else {
      setError('שם משתמש או סיסמה אינם נכונים');
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Title>ברוכים הבאים</Title>
        <Subtitle>הזן את פרטיך כדי להיכנס למערכת</Subtitle>

        <form onSubmit={handleLogin} style={{ width: '100%' }}>
          <InputGroup>
            <Icon hasError={error && !username}><FiUser size={18} /></Icon>
            <Input
              type="text"
              placeholder="שם משתמש"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              hasError={error && !username}
            />
          </InputGroup>

          <InputGroup>
            <Icon hasError={error && !password}><FiLock size={18} /></Icon>
            <Input
              type="password"
              placeholder="סיסמה"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hasError={error && !password}
            />
          </InputGroup>

          {/* "Remember Me" Toggle */}
          <RememberMeContainer>
            <Checkbox
              type="checkbox"
              checked={rememberMe}
              onChange={() => setRememberMe(!rememberMe)}
            />
            זכור אותי
          </RememberMeContainer>

          <Button type="submit">התחבר</Button>
        </form>

        {error && <ErrorMessage>{error}</ErrorMessage>}
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;
